<script>
import axios from 'axios';
import Loader from './Loader.vue';
import dayjs from 'dayjs';
import GetHiringEventSlotsMixin from '../mixins/GetHiringEventSlotsMixin';
import PrimaryButton from './Button.vue';
import BreakPointsMixin from "../mixins/BreakPointsMixin";

export default {
  components: {
    PrimaryButton,
    Loader,
  },
  props: {
    jobId: String|Number,
    applicantId: String|Number,
    color: String,
    nextUrl: String,
    isThankyouPage: {
      type: Boolean,
      default: false
    },
    rsvpJobSlot: {
      type: Boolean,
      default: false
    },
    submissionItemTempId: {
      type: String,
      default: ""
    },
    isNurtureRsvp: {
      type: Boolean,
      default: false
    }
  },

  mixins: [
    BreakPointsMixin,
    GetHiringEventSlotsMixin
  ],

  data() {
    return {
      error: null,
      slots: null,
      selectedSlot: null,
      confirmingSlot: false,
      selectedSlotNoLongerAvailable: false,

      // For mobile view display only. When in mobile view, the user has to
      // select a date first
      mobileSelectedDate: null,
    };
  },

  computed: {
    isMobileView() {
      return this.isBreakPointDown('md');
    },

    groupedSlots() {
      return (this.slots || []).reduce((aggregator, item) => {
        if (!item.date) return aggregator; // Prevent errors if date is missing

        // Fix Safari Date Parsing (Replace "-" with "/" for compatibility)
        const safeDate = item.date.replace(/-/g, '/');

        if (!dayjs(safeDate).isValid()) return aggregator; // Ignore invalid dates

        if (!aggregator[safeDate]) {
          aggregator[safeDate] = {
            dateInfo: {
              date: dayjs(safeDate).format('MMM D'),
              day: dayjs(safeDate).format('ddd'),
            },
            slots: [],
          };
        }
        aggregator[safeDate].slots.push(item);
        return aggregator;
      }, {});
    },
  },

  mounted() {
    this.loadSlots();
  },

  methods: {
    async loadSlots() {
      const response = await axios.get(`/jobs/${this.jobId}/rsvp_slots`, {'rsvpJobSlot': this.rsvpJobSlot});

      if (response.status === 200 && response.data.success) {
        this.slots = this.getHiringEventSlots(response.data.slots || []);
      }
    },
    toggleSlot(slotId) {
      this.selectedSlot = this.selectedSlot !== slotId ? slotId : null;
      this.selectedSlotNoLongerAvailable = false;
    },

    toggleMobileSelectDate(date) {
      this.mobileSelectedDate = this.mobileSelectedDate !== date ? date : null;
      this.selectedSlot = null;
    },

    async confirmSlot() {
      if(this.applicantId == "") {
        window.location.href = this.nextUrl;
      }
      this.error = null;
      if (!this.selectedSlot) return;

      this.confirmingSlot = true;

      // Check availability again:
      await this.loadSlots();

      const targetSlot = this.slots.find(s => s.id === this.selectedSlot);

      if (targetSlot.full) {
        this.selectedSlotNoLongerAvailable = true;
        this.confirmingSlot = false;
        return;
      }

      try {
        let response;
        if(this.isNurtureRsvp){
          response = await axios.post(
            `/jobs/${this.jobId}/nurture_rsvp/${this.selectedSlot}/${this.applicantId}`, {startTime: targetSlot.originalStartTime12Hour, endTime: targetSlot.originalEndTime12Hour, submissionItemTempId: this.submissionItemTempId}
          );
        } else {
          response = await axios.post(
            `/jobs/${this.jobId}/rsvp/${this.selectedSlot}/${this.applicantId}`, {startTime: targetSlot.originalStartTime12Hour, endTime: targetSlot.originalEndTime12Hour, submissionItemTempId: this.submissionItemTempId}
          );
        }

        if (response.status === 200 && response.data.success) {
          if(this.isThankyouPage) {
            const timeSlotPickerDiv = document.getElementById('timeSlotPicker');
            if (timeSlotPickerDiv) {
              timeSlotPickerDiv.style.display = 'none';
            }
            const url = new URL(window.location.href);
            if (url.searchParams.has('rsvp_skip')) {
              url.searchParams.delete('rsvp_skip');
              // Update the URL and reload the page
              window.location.href = url.toString();
            } else {
              // Reload the page if 'rsvp_skip' is not in the URL
              window.location.reload();
            }
          } else {
            window.location.href = this.nextUrl;
          }
        } else {
          this.error = 'Unexpected response from server.'
        }
      } catch (e) {
        this.error = e.toString();
      }

      this.confirmingSlot = false;
    },
    cellStyle(slot) {
      const color = this.color ?? '#1F4484';
      const hasSelection = this.selectedSlot === slot.id;
      return {
        border: `3px solid ${color}`,
        cursor: !slot.full ? 'pointer' : 'not-allowed',
        backgroundColor: hasSelection ? color : '',
        color: hasSelection ? 'white' : '',
        padding: '15px 10px',
        borderRadius: '5px',
        opacity: slot.full ? 0.5 : 1,
      };
    },
  },
};
</script>

<template>
  <loader :loading="slots === null" loading-text="Loading time slots">
    <div>
      <div :class="`picker-wrapper ${isMobileView ? 'mobile' : ''} ${isMobileView && mobileSelectedDate ? 'has-mobile-selection' : ''}`">
        <div style="display: inline-block">
          <div class="picker-options">
            <div
              v-for="date in Object.keys(groupedSlots).sort()"
              style="display: flex; flex-direction: column; row-gap: 10px"
              :class="`column ${mobileSelectedDate === date ? 'mobile-selected' : ''}`"
            >
              <div
                @click="isMobileView && toggleMobileSelectDate(date)"
                class="column-head"
                :style="{
                    ...((groupedSlots[date].slots.find(s => s.id === selectedSlot) || isMobileView) ? {
                      borderColor: color
                    } : {}),
                    ...((isMobileView && mobileSelectedDate === date) ? {
                        backgroundColor: color,
                        color: 'white',
                      } : {})
                }"
              >
                <div class="date">{{ groupedSlots[date].dateInfo.date }}</div>
                <div class="day">{{ groupedSlots[date].dateInfo.day }}</div>
              </div>
              <div
                v-if="!isMobileView || (date === mobileSelectedDate)"
                class="option"
                v-for="slot in groupedSlots[date].slots.sort(
                  (s1, s2) =>
                    s1.dayjsStartDate.unix() - s2.dayjsStartDate.unix(),
                )"
                v-bind:key="slot.id"
                :style="cellStyle(slot)"
                @click="!slot.full && toggleSlot(slot.id)"
                :title="
                  !slot.full ? 'Click to select this slot' : 'Slot unavailable'
                "
              >
                {{ slot.originalStartTime12Hour }} {{ slot.originalEndTime12Hour ? ' - ' + slot.originalEndTime12Hour : '' }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="next-buttons">
        <a v-if="!isThankyouPage && !isNurtureRsvp">
          <flat-button
            :href="nextUrl+'&rsvp_skip=1'"
            title="Skip for Now"
            class="w-full"
            style="color: rgb(32, 32, 40)"
          >
            Skip for Now
          </flat-button>
        </a>
        <a>
          <primary-button
            @click="confirmSlot"
            title="Skip for Now"
            class="w-full"
            :color="color"
            :disabled="!selectedSlot || confirmingSlot || selectedSlotNoLongerAvailable"
          >
            Confirm {{ isThankyouPage ? "RSVP" : "" }}
          </primary-button>
        </a>
      </div>
      <div v-if="selectedSlotNoLongerAvailable" class="confirm-error">
        We're sorry, the slot you selected is no longer available. Please select
        a different slot.
      </div>
      <div v-if="error" class="confirm-error">{{ error}}</div>
    </div>
  </loader>
</template>

<style scss scoped>
.picker-wrapper {
  margin-top: 25px;
  display: block;
  text-align: center;
  overflow-y: auto;

  .column {
    min-width: 150px;

    .column-head {
      margin-bottom: 15px;
      border-radius: 4px;
      padding: 20px;
      border-style: solid;
      border-width: 2px;
      border-color: white;

      .date {
        font-size: 18px;
      }

      .day {
        font-size: 22px;
        font-weight: 600;
      }
    }

    .option {
      font-weight: 600;
    }
  }

  .picker-options {
    display: flex;
    flex-direction: row;
    column-gap: 25px;
    padding-bottom: 15px;
    user-select: none;
  }

  &.mobile {
    .column {
      .column-head {
        cursor: pointer;
        margin-bottom: 0;

        .has-mobile-selection .mobile-selected & {
        }
      }

      .has-mobile-selection &.mobile-selected {
        margin-bottom: 25px;
      }
    }


    .picker-options {
      display: flex;
      flex-direction: column;
      row-gap: 25px;
    }
  }
}

.next-buttons {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 100px;
  margin-top: 35px;

  @media (max-width: 1000px) {
    gap: 10%;
  }
}

.confirm-error {
  padding: 25px;
  font-size: 16px;
  text-align: center;
}
</style>
